import { useEffect, useState } from 'react';
import { NudosButton } from '../../../NudosComponents';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';
import { updateConfigurationStatus } from '../../../../services/product-configuration.service';
import {
  TProductConfigResponse,
  LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID,
  CONFIG_STATUS_ID,
  CONFIGURATION_CLASS_TYPE,
  CONFIG_EVIDENCE_FIELD_NAME_ID,
  CONFIGURATION_STATUS_MODAL_VALUES
} from '../../../../types/product.configuration.types';
import { getConfigurationDisplayElements } from '../../../../utils/logisticServices.utils';
import NotCompletedConfigurationFields from './NotCompletedConfigurationFields/NotCompletedConfigurationFields';
import CompletedConfigurationFields from './CompletedConfigurationFields/CompletedConfigurationFields';
import { sendRequireConfirmConfigurationEmail } from '../../../../services/orders';
import {
  hasConfigRequiredAdditionalAction,
  getFileUrlsFromEvidence,
  getNotCompletedReasonIdFromEvidence,
  getCommentsFromEvidence
} from '../../../../utils/configurations';

import './ConfigurationStatusModal.scss';

const ConfigurationStatusModalContent = ({
  type,
  configurationData,
  closeModalCallback,
  updateDataCallback,
  orderId
}: {
  type: CONFIGURATION_STATUS_MODAL_VALUES;
  configurationData: TProductConfigResponse;
  closeModalCallback: () => void;
  updateDataCallback: () => Promise<void>;
  orderId?: string | null;
}) => {
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [csvFileToUpload, setCsvFileToUpload] = useState<File>();
  const [loading, setLoading] = useState<boolean>(false);
  const [notCompletedData, setNotCompletedData] = useState<InotCompletedData>();
  const [evidenceComments, setEvidenceComments] = useState<string>('');

  const { uuid, evidence, configClassId, configClassName } = configurationData;

  const imageUrls = getFileUrlsFromEvidence(evidence);
  const initialNotCompletedReasonId = getNotCompletedReasonIdFromEvidence(evidence);
  const initialOtherReason = getCommentsFromEvidence(evidence);
  const imageUrl = imageUrls?.[0];

  const isCompletedModal = type === CONFIGURATION_STATUS_MODAL_VALUES.COMPLETED;
  const isSeeReasonsModal = type === CONFIGURATION_STATUS_MODAL_VALUES.SEE_NOT_COMPLETED_REASONS;
  const currentFileUrl = isSeeReasonsModal ? imageUrl : undefined;
  const configurationIsHashId = configClassName === CONFIGURATION_CLASS_TYPE.HASH_ID;
  const showTextArea = isCompletedModal && configClassName === CONFIGURATION_CLASS_TYPE.CREATE_ADMIN;
  const configStatusId = isCompletedModal ? CONFIG_STATUS_ID.COMPLETED : CONFIG_STATUS_ID.NOT_COMPLETED;

  const isOtherReason =
    notCompletedData?.logisticConfigurationNotCompletedReasonId === LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID.OTHER;
  const incompleteOtherReason =
    isOtherReason && (!notCompletedData?.otherReasonComment || notCompletedData.otherReasonComment.length <= 5);

  const hasDataChanges = () => {
    if (!isSeeReasonsModal || !initialNotCompletedReasonId) return true;
    return (
      !!fileToUpload ||
      notCompletedData?.logisticConfigurationNotCompletedReasonId !== initialNotCompletedReasonId ||
      notCompletedData?.otherReasonComment !== initialOtherReason
    );
  };

  const completedModalIsDisabled = () => {
    if (configurationIsHashId) return !csvFileToUpload;
    if (showTextArea) return !fileToUpload;
    return !fileToUpload && !currentFileUrl;
  };

  const notCompletedModalIsDisabled = () => {
    const noFile = !fileToUpload && !currentFileUrl;
    const incompleteData = !notCompletedData?.logisticConfigurationNotCompletedReasonId || incompleteOtherReason;
    return incompleteData || noFile || !hasDataChanges();
  };

  const disableButton = () => {
    if (isCompletedModal) return completedModalIsDisabled();
    return notCompletedModalIsDisabled();
  };

  const isButtonDisabled = disableButton();

  const getDataByType = () => {
    const { text } = getConfigurationDisplayElements(configurationData);
    switch (type) {
      case CONFIGURATION_STATUS_MODAL_VALUES.COMPLETED:
        return { subtitle: `Sube una evidencia de la configuración ${text} para marcarla como completada` };
      default:
        return { subtitle: `Indica el motivo por el cual no se completó la configuración ${text}` };
    }
  };

  const uploadProofFile = (file?: File) => setFileToUpload(file);
  const uploadCsvFile = (file?: File) => setCsvFileToUpload(file);

  const getConfigEvidence = () => {
    if (isCompletedModal) {
      return evidenceComments
        ? [{ fieldNameId: CONFIG_EVIDENCE_FIELD_NAME_ID.COMMENTS, fieldValue: evidenceComments }]
        : undefined;
    }
    const { logisticConfigurationNotCompletedReasonId, otherReasonComment } = notCompletedData || {};
    if (!logisticConfigurationNotCompletedReasonId) return undefined;
    const items = [
      {
        fieldNameId: CONFIG_EVIDENCE_FIELD_NAME_ID.NOT_COMPLETED_REASON,
        fieldValue: String(logisticConfigurationNotCompletedReasonId)
      }
    ];
    if (isOtherReason && otherReasonComment) {
      items.push({ fieldNameId: CONFIG_EVIDENCE_FIELD_NAME_ID.COMMENTS, fieldValue: otherReasonComment });
    }
    return items;
  };

  const handleSubmit = async () => {
    if (isButtonDisabled || !uuid) return;
    setLoading(true);
    try {
      const files = [fileToUpload, csvFileToUpload].filter(Boolean) as File[];
      await updateConfigurationStatus({ uuid, configStatusId, configEvidence: getConfigEvidence() }, files);
      if (
        orderId &&
        configStatusId === CONFIG_STATUS_ID.COMPLETED &&
        hasConfigRequiredAdditionalAction(configClassId)
      ) {
        await sendRequireConfirmConfigurationEmail(Number(orderId));
      }
      await updateDataCallback();
      closeModalCallback();
      displaySuccessNotification({ customJSXMessage: <>Configuración actualizada con éxito</> });
    } catch {
      displayErrorNotification();
    } finally {
      setLoading(false);
    }
  };

  const { subtitle } = getDataByType();

  useEffect(() => {
    if (notCompletedData || !initialNotCompletedReasonId) return;
    setNotCompletedData({
      logisticConfigurationNotCompletedReasonId: initialNotCompletedReasonId,
      otherReasonComment: initialOtherReason || undefined
    });
  }, [initialNotCompletedReasonId, initialOtherReason]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`configurationStatusModalContent ${type}`}>
      <div className={`subtitle orangeText${showTextArea ? ' hasTextArea' : ''}`}>{subtitle}</div>
      {isCompletedModal && (
        <CompletedConfigurationFields
          configurationData={configurationData}
          evidenceComments={evidenceComments}
          setEvidenceComments={setEvidenceComments}
          uploadProofFile={uploadProofFile}
          uploadCsvFile={uploadCsvFile}
        />
      )}
      {!isCompletedModal && (
        <NotCompletedConfigurationFields
          type={type}
          updateDataCallback={setNotCompletedData}
          initialData={notCompletedData}
          uploadProofFile={uploadProofFile}
          configurationData={configurationData}
        />
      )}
      <div className="verificationQuestion orangeText">¿Seguro que deseas continuar?</div>
      <NudosButton
        componentSize="small"
        buttonText={`Marcar como ${!isCompletedModal ? 'no' : ''} completada`}
        handleClick={handleSubmit}
        isButtonDisabled={isButtonDisabled}
        isButtonLoading={loading}
      />
    </div>
  );
};

export default ConfigurationStatusModalContent;

export interface InotCompletedData {
  logisticConfigurationNotCompletedReasonId: number;
  otherReasonComment?: string;
}
